import * as React from 'react';
import classNames from 'classnames';
import { isEmptyNavItem } from '../isEmptyNavItem';
import { useNavContext } from '../NavContext';
import { useHoverIntent } from '../../../../hooks/useHoverIntent';
import { TextNavFlyout } from '../DesktopNav/TextNavFlyout';
import { BookendsBadge } from '../../../BookendsBadge';
import TrackableLink from '../../../TrackableLink';
import { NavItemLeafType, NavItemWithChildrenType } from '../../../../types';
import { TOP_NAV_LEVEL } from '../../../../../constants/analytics';
import { HOLIDAY_RED_COLOR_CODE } from '../../../../../constants/styleClasses';
import { isThemeHoliday } from '../../../../utils/utilities';

export const TextNavItemWithChildren = (
  props: NavItemWithChildrenType & React.ComponentProps<'li'>
) => {
  const {
    navigation,
    hoverNavItem,
    unfocusNav,
    keypress,
    addItemRef,
    isMxNav,
  } = useNavContext();
  const flyoutId = React.useId();
  const labelId = React.useId();

  const itemRef = useHoverIntent<HTMLLIElement>({
    hoverIn: () => {
      hoverNavItem(props.index, navigation);
    },
    hoverOut: () => {
      unfocusNav();
    },
  });
  addItemRef(props.index, itemRef);

  const url =
    props.url || (props.children.find(isEmptyNavItem) as NavItemLeafType)?.url;
  const navigationDetailPath = props.analyticsId ? [props.analyticsId] : [];

  return (
    <li
      ref={itemRef}
      className={classNames(
        'site-header-nav-item',
        'site-header-nav-toplevel',
        'site-header-nav-toplevel-js',
        'site-header-nav-text-layout-js',
        props.className
      )}
      data-item-title={props.text}
      onKeyDown={(event: React.KeyboardEvent<HTMLLIElement>) => {
        event.persist();
        event.preventDefault();        
        if (
          event.key === 'Enter' &&
          event.currentTarget.contains(document.activeElement) &&
          document.activeElement?.tagName !== 'A'
        ) {
          const link = event.currentTarget.querySelector<HTMLAnchorElement>(
            'a.site-header-nav-link'
          );
          link?.click();
          return;
        }
         keypress(props.index, event, navigation);
      }}
      onBlur={(event) => {
        if (
          event.relatedTarget?.classList === undefined ||
          event.relatedTarget?.classList.contains('search-bar-input')
        ) {
          unfocusNav(true);
        }
      }}
      tabIndex={0}
    >
      <TrackableLink
        subSection="TopNav"
        navigationDetailPath={navigationDetailPath}
        className={`site-header-nav-link ${props.theme}`}
        href={url}
        dataPosition={props.dataPosition}
        aria-haspopup="true"
        aria-controls={flyoutId}
        aria-expanded={props.isInFocusedCategory}
        navigationSection={isMxNav ? TOP_NAV_LEVEL : undefined}
        style={
          isThemeHoliday(props.theme) ? { color: HOLIDAY_RED_COLOR_CODE } : {}
        }
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === ' ') {
            event.preventDefault();
            hoverNavItem(props.index, navigation);
          }
        }}
      >
        <span id={labelId}>
          {props.text}
          {props.badge && <BookendsBadge {...props.badge} layout="visual" />}
        </span>
      </TrackableLink>
      <TextNavFlyout
        children={props.children}
        text={props.text}
        parentNavigationDetailPath={navigationDetailPath}
        id={flyoutId}
        labelledBy={labelId}
        className={props.className}
        hasMx={props.children?.some((child) => child?.startOfMx)}
        isInFocusedCategory={props.isInFocusedCategory}
        onFocus={props.onFocus}
        seeAllText={props.seeAllCategory}
        seeAllAnalyticsId={props.analyticsId}
        seeAllUrl={props.url}
      />
    </li>
  );
};
